import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithArgs } from "../utils/reduxHelper";

export const manualSourcingAPI = createApi({
  reducerPath: "manualSourcingAPI",
  baseQuery: baseQueryWithArgs,
  tagTypes: ["ManualCatalog", "SourcingTask"],
  endpoints: (builder) => ({
    submitManualSourcingLinksAsync: builder.mutation({
      query: ({ brandId, urls, isBrandSourcing = false }) => ({
        url: "s/submitManualSourcingLinksAsync",
        method: "POST",
        body: {
          brand_id: brandId,
          urls: urls.filter((url) => url.trim()),
          is_brand_sourcing: isBrandSourcing,
          creator_username:
            localStorage.getItem("username")?.replaceAll('"', "") || "",
        },
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to submit links");
        }
      },
    }),

    getScrapingTaskStatus: builder.query({
      query: (taskId) => {
        const creator =
          localStorage.getItem("username")?.replaceAll('"', "") || "";
        const params = new URLSearchParams({
          ...(creator && { creator_username: creator }),
        });
        return `s/scraping-task-status/${taskId}?${params}`;
      },
      providesTags: ["SourcingTask"],
      transformResponse: (response) => {
        if (response?.state) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to get task status");
        }
      },
    }),

    getManualSourcingCatalog: builder.query({
      query: ({ brandId, includeRawData = false, isBrandSourcing = false }) => {
        const creator =
          localStorage.getItem("username")?.replaceAll('"', "") || "";
        const params = new URLSearchParams({
          brand_id: brandId,
          include_raw_data: includeRawData.toString(),
          is_brand_sourcing: isBrandSourcing.toString(),
          ...(creator && { creator: creator }),
        });
        return `s/getManualSourcingCatalog?${params}`;
      },
      providesTags: ["ManualCatalog"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to get catalog");
        }
      },
    }),

    addManualSourcingItemToCart: builder.mutation({
      query: ({ variantId, quantity = 1, brandSourcingBrandId = null }) => ({
        url: "s/addManualSourcingItemToCart",
        method: "POST",
        body: {
          variant_id: variantId,
          quantity: quantity,
          brand_sourcing_brand_id: brandSourcingBrandId,
          creator_username:
            localStorage.getItem("username")?.replaceAll('"', "") || "",
        },
      }),
      invalidatesTags: ["getCartProducts"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to add item to cart");
        }
      },
    }),

    updateManualCatalogItem: builder.mutation({
      query: ({
        catalogItemId, // Required - catalog item to update
        brandId,
        url,
        title,
        price,
        size,
        color,
        imageFile,
        isBrandSourcing = false,
        triggerScraping = false,
      }) => {
        if (imageFile && imageFile.type !== "image/png") {
          throw new Error("Only PNG images are allowed");
        }

        const formData = new FormData();
        formData.append("brand_id", brandId);
        formData.append("url", url);
        formData.append("title", title);
        formData.append("price", price);
        formData.append("size", size);
        formData.append("color", color);
        formData.append("is_brand_sourcing", isBrandSourcing.toString());
        formData.append("trigger_scraping", triggerScraping.toString());
        formData.append(
          "creator_username",
          localStorage.getItem("username")?.replaceAll('"', "") || ""
        );

        // catalog_item_id is always required for updates
        formData.append("catalog_item_id", catalogItemId);

        if (imageFile) {
          formData.append("image_file", imageFile);
        }

        return {
          url: "s/updateManualCatalogItem",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["ManualCatalog"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to update catalog item");
        }
      },
    }),

    getSourcingConstant: builder.query({
      query: () => {
        const creator =
          localStorage.getItem("username")?.replaceAll('"', "") || "";
        const params = new URLSearchParams({
          ...(creator && { creator_username: creator }),
        });
        return `s/getSourcingConstant?${params}`;
      },
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        } else {
          throw new Error(
            response?.message || "Failed to get sourcing constant"
          );
        }
      },
    }),
  }),
});

export const {
  useSubmitManualSourcingLinksAsyncMutation,
  useGetScrapingTaskStatusQuery,
  useLazyGetScrapingTaskStatusQuery,
  useGetManualSourcingCatalogQuery,
  useLazyGetManualSourcingCatalogQuery,
  useAddManualSourcingItemToCartMutation,
  useUpdateManualCatalogItemMutation,
  useGetSourcingConstantQuery,
  useLazyGetSourcingConstantQuery,
} = manualSourcingAPI;
